import { LitElement, html, css, nothing } from 'lit';
import { StoreMixin } from '../store.js';
import { api } from '../api.js';
import { navigate } from '../router.js';
import { count, hours as fmtHours, since } from '../lib/format.js';
import { sizeTierLabel } from '../lib/size.js';
import { addTask, moveTask, normalizeOutline, removeTask, reparentTask } from '../lib/outline.js';

/** Long enough that typing into an hours box does not fire a request per keystroke. */
const SAVE_DEBOUNCE_MS = 400;

/**
 * The Template Designer (Estimate Tool 8.10; Phase E14) — `/estimate/settings/templates/:tier`.
 *
 * One rung's template: its published version, the draft open on it if there is one, and the
 * history the account's own projects of this size wrote beside every line. An admin opens a draft
 * from the published version, edits the outline — add, rename, move, remove groups and tasks; set
 * each task's default assignments by service and hours; set each task's timing in working days —
 * and publishes it, with the rules of 8.10 run first and every refusal naming its line.
 *
 * Five things the screen is particular about.
 *
 * **The draft persists on every change.** Each edit is applied locally through the same pure
 * functions the server normalises with (`lib/outline.js`) and the whole outline is sent; the
 * response — with the server's ids and its findings — replaces what is on screen.
 *
 * **Removing a line removes it.** This is a draft: nothing has been written from it and nothing
 * will be, so the flag-not-delete rule a plan lives under (8.7) does not apply, and the outline
 * renumbers. Removing a group removes its children.
 *
 * **History is advice.** The column beside each line says what the account's projects of this
 * size booked — median hours and sample, and how many schedules use the task at all — and it
 * changes nothing. What is published is what the generator cuts.
 *
 * **The size range is its own control.** An hour added to the content audit does not move where
 * the rung sits on the size axis; the range does, and it is set deliberately, here.
 *
 * **No control names a person.** A template line is a service and its hours (8.3). There is no
 * field for anyone.
 */
class EstimateTemplateDesigner extends StoreMixin(LitElement) {
  static properties = {
    route: { type: Object },
    _template: { state: true },
    _version: { state: true },
    _history: { state: true },
    _services: { state: true },
    _diff: { state: true },
    _viewing: { state: true },
    _saving: { state: true },
    _busy: { state: true },
    _error: { state: true },
    _loadError: { state: true },
    _collapsed: { state: true },
    _showHistory: { state: true },
    _range: { state: true },
    _publishNote: { state: true },
  };

  constructor() {
    super();
    this._template = null;
    this._version = null;
    this._history = null;
    this._services = [];
    this._diff = null;
    // Which version the outline shows: the draft while one is open, else the published one, else
    // whichever superseded version the reader opened to look at.
    this._viewing = null;
    this._saving = false;
    this._busy = false;
    this._error = null;
    this._loadError = null;
    this._collapsed = new Set();
    this._showHistory = true;
    this._range = null;
    this._publishNote = '';
    this._timer = null;
    this._bootedFor = null;
  }

  static styles = css`
    :host {
      display: block;
    }
    .card {
      background: var(--surface);
      border: 1px solid var(--border-soft);
      border-radius: var(--radius-card);
      box-shadow: var(--shadow-card);
      padding: 20px 24px 18px;
      margin-bottom: 18px;
    }
    .eyebrow {
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--ink-muted);
    }
    h2 {
      margin: 6px 0 4px;
      font-size: 20px;
      color: var(--ink-strong);
      text-transform: capitalize;
    }
    h3 {
      margin: 0 0 10px;
      font-size: 14px;
      color: var(--ink-strong);
    }
    .sub {
      margin: 0 0 14px;
      font-size: 13px;
      color: var(--ink-muted);
      max-width: 72ch;
      line-height: 1.5;
    }
    .totals {
      display: grid;
      gap: 14px 22px;
      grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
      margin: 12px 0 4px;
    }
    .stat .k {
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--ink-muted);
      display: block;
    }
    .stat .v {
      font-size: 19px;
      font-weight: 700;
      font-variant-numeric: tabular-nums;
      color: var(--ink-strong);
    }
    .stat .n {
      font-size: 12px;
      color: var(--ink-muted);
    }
    .actions {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
      margin-top: 16px;
      padding-top: 14px;
      border-top: 1px solid var(--border-soft);
    }
    button {
      font: inherit;
      font-size: 13px;
      font-weight: 600;
      padding: 8px 16px;
      border: 1px solid var(--control-border);
      border-radius: 6px;
      background: var(--surface);
      color: var(--ink-strong);
      cursor: pointer;
    }
    button.primary {
      background: var(--ink-strong);
      color: var(--surface);
      border-color: var(--ink-strong);
    }
    button.danger {
      color: var(--status-overloaded);
    }
    button[disabled] {
      opacity: 0.5;
      cursor: not-allowed;
    }
    button.link {
      border: 0;
      background: none;
      padding: 0;
      font: inherit;
      font-size: 12px;
      color: var(--ink-muted);
      text-decoration: underline;
      cursor: pointer;
    }
    input,
    select {
      font: inherit;
      font-size: 13px;
      padding: 5px 8px;
      border: 1px solid var(--control-border);
      border-radius: 6px;
      background: var(--control-bg);
      color: var(--ink-strong);
      box-sizing: border-box;
    }
    input[type='number'] {
      width: 7ch;
      text-align: right;
      font-variant-numeric: tabular-nums;
    }
    input:disabled,
    select:disabled {
      opacity: 0.6;
    }
    .error {
      color: var(--status-overloaded);
      font-weight: 600;
      font-size: 13px;
    }
    .state {
      color: var(--ink-muted);
      padding: 16px 0;
    }
    .hint {
      font-size: 12px;
      color: var(--ink-muted);
      line-height: 1.45;
    }
    .findings {
      margin: 12px 0 0;
      padding: 10px 12px;
      border: 1px solid var(--border);
      border-left: 3px solid var(--ink-muted);
      border-radius: 6px;
      background: var(--surface-2);
      font-size: 13px;
      line-height: 1.5;
    }
    .findings.hard {
      border-left-color: var(--status-overloaded);
    }
    .findings ul {
      margin: 4px 0 0;
      padding-left: 18px;
    }
    .findings strong {
      color: var(--ink-strong);
    }
    .range {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      font-size: 13px;
    }
    .locked {
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--ink-muted);
    }
    .scroll {
      overflow-x: auto;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 13px;
    }
    th {
      text-align: left;
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--ink-muted);
      padding: 6px 8px;
      border-bottom: 1px solid var(--border);
      white-space: nowrap;
    }
    td {
      padding: 5px 8px;
      border-bottom: 1px solid var(--border-soft);
      vertical-align: top;
    }
    td.num,
    th.num {
      text-align: right;
      font-variant-numeric: tabular-nums;
    }
    tr.group > td {
      background: var(--surface-2);
      font-weight: 700;
      color: var(--ink-strong);
    }
    tr.flagged > td:first-child {
      box-shadow: inset 3px 0 0 var(--status-overloaded);
    }
    .name {
      font: inherit;
      border: 1px solid transparent;
      background: none;
      color: inherit;
      padding: 4px 6px;
      border-radius: 5px;
      width: 100%;
      min-width: 16ch;
      box-sizing: border-box;
      font-weight: inherit;
    }
    .name:hover,
    .name:focus {
      border-color: var(--control-border);
      background: var(--control-bg);
    }
    .indent-1 {
      padding-left: 22px;
    }
    .indent-2 {
      padding-left: 40px;
    }
    .indent-3 {
      padding-left: 58px;
    }
    .lines {
      display: grid;
      gap: 4px;
    }
    .line {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;
    }
    .line select {
      width: auto;
      min-width: 13ch;
      padding: 3px 6px;
      font-size: 12px;
    }
    .line input[type='number'] {
      padding: 3px 6px;
      font-size: 12px;
    }
    .timing {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      color: var(--ink-muted);
      white-space: nowrap;
    }
    .timing input {
      width: 6ch;
    }
    .icon {
      padding: 2px 7px;
      font-size: 12px;
      line-height: 1.4;
    }
    .rowtools {
      display: flex;
      gap: 4px;
      flex-wrap: wrap;
      justify-content: flex-end;
    }
    .rowtools select {
      padding: 2px 4px;
      font-size: 12px;
      max-width: 14ch;
    }
    .hist {
      font-size: 11.5px;
      color: var(--ink-muted);
      font-variant-numeric: tabular-nums;
      line-height: 1.45;
      white-space: nowrap;
    }
    .hist strong {
      color: var(--ink);
      font-weight: 600;
    }
    .hist .rare {
      color: var(--status-caution, var(--ink-strong));
    }
    .mark {
      font-family: var(--font-eyebrow);
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      padding: 1px 5px;
      border-radius: 4px;
      border: 1px solid var(--border-soft);
      color: var(--ink-muted);
      white-space: nowrap;
    }
    .mark.warn {
      color: var(--status-caution, var(--ink-strong));
      border-color: currentColor;
    }
    .mark.hard {
      color: var(--status-overloaded);
      border-color: currentColor;
    }
    .toolbar {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
      margin-bottom: 10px;
    }
    .toolbar label {
      display: flex;
      align-items: center;
      gap: 6px;
      font-weight: 600;
      font-size: 12px;
      color: var(--ink-muted);
    }
    .toolbar input[type='checkbox'] {
      width: auto;
    }
    .saving {
      font-size: 12px;
      color: var(--ink-muted);
      margin-left: auto;
    }
    .versions {
      display: grid;
      gap: 6px;
      font-size: 13px;
    }
    .version {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
      padding: 6px 0;
      border-bottom: 1px solid var(--border-soft);
    }
    .version .n {
      color: var(--ink-muted);
      font-size: 12px;
    }
    .diff {
      display: grid;
      gap: 6px;
      font-size: 13px;
    }
    .diff ul {
      margin: 0;
      padding-left: 18px;
      color: var(--ink);
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this._boot();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this._timer);
  }

  updated(changed) {
    if (changed.has('route') && this._bootedFor !== this._tier) this._boot();
  }

  get _tier() {
    return this.route?.params?.tier ?? null;
  }

  get _isAdmin() {
    return this.appState.user?.effective_role === 'admin';
  }

  /** Editing is for an admin, on a draft, and nothing else. */
  get _editable() {
    return this._isAdmin && this._version?.status === 'draft';
  }

  async _boot() {
    this._bootedFor = this._tier;
    if (!this._tier) return;
    try {
      const [template, services] = await Promise.all([
        api.get(`/estimate/templates/${this._tier}`),
        api.get('/estimate/schedule/services').catch(() => ({ services: [] })),
      ]);
      this._template = template;
      this._services = services.services ?? [];
      this._range = template.size_range ? { ...template.size_range } : { min: '', max: '' };
      const open = template.draft ?? template.published ?? null;
      await this._show(open?.id ?? null);
    } catch (err) {
      this._loadError = `Could not open this template: ${err.message}`;
    }
  }

  /** Put a version on screen, with its history and — for a draft — its diff. */
  async _show(versionId) {
    if (!versionId) {
      this._version = null;
      this._history = await api.get(`/estimate/templates/${this._tier}/history`).catch(() => null);
      this._diff = null;
      return;
    }
    const version = await api.get(`/estimate/templates/${this._tier}/versions/${versionId}`);
    this._version = { ...version, tasks: normalizeOutline(version.tasks) };
    this._viewing = versionId;
    const [history, diff] = await Promise.all([
      api
        .get(`/estimate/templates/${this._tier}/history?version_id=${versionId}`)
        .catch(() => null),
      version.status === 'draft'
        ? api.get(`/estimate/templates/${this._tier}/versions/${versionId}/diff`).catch(() => null)
        : Promise.resolve(null),
    ]);
    this._history = history;
    this._diff = diff;
  }

  async _reloadHead() {
    this._template = await api.get(`/estimate/templates/${this._tier}`);
  }

  // ------------------------------------------------------------------------------------------
  // Versions: open a draft, publish, discard, restore.

  async _openDraft(fromVersionId = null) {
    this._busy = true;
    this._error = null;
    try {
      const draft = await api.post(`/estimate/templates/${this._tier}/versions`, {
        from_version_id: fromVersionId,
      });
      await this._reloadHead();
      await this._show(draft.id);
    } catch (err) {
      this._error = err.message;
    } finally {
      this._busy = false;
    }
  }

  async _publish() {
    if (!this._version) return;
    await this._flush();
    this._busy = true;
    this._error = null;
    try {
      const published = await api.post(
        `/estimate/templates/${this._tier}/versions/${this._version.id}/publish`,
        { note: this._publishNote || null },
      );
      this._publishNote = '';
      await this._reloadHead();
      await this._show(published.id);
    } catch (err) {
      // The refusal names its lines; they are shown where the lines are as well as here.
      if (err.details?.hard) {
        this._version = {
          ...this._version,
          findings: { hard: err.details.hard, soft: err.details.soft ?? [] },
        };
      }
      this._error = err.message;
    } finally {
      this._busy = false;
    }
  }

  async _discard() {
    if (!this._version || this._version.status !== 'draft') return;
    if (!window.confirm('Discard this draft? The published version is not affected.')) return;
    this._busy = true;
    this._error = null;
    try {
      clearTimeout(this._timer);
      this._queued = false;
      await api.delete(`/estimate/templates/${this._tier}/versions/${this._version.id}`);
      await this._reloadHead();
      await this._show(this._template.published?.id ?? null);
    } catch (err) {
      this._error = err.message;
    } finally {
      this._busy = false;
    }
  }

  async _saveRange() {
    this._busy = true;
    this._error = null;
    try {
      const head = await api.put(`/estimate/templates/${this._tier}/size-range`, {
        min: Number(this._range.min),
        max: Number(this._range.max),
      });
      this._template = { ...this._template, ...head };
      this._range = { ...head.size_range };
    } catch (err) {
      this._error = err.message;
    } finally {
      this._busy = false;
    }
  }

  // ------------------------------------------------------------------------------------------
  // Editing. Every change goes through the pure outline functions and is then saved whole.

  _apply(tasks) {
    this._version = { ...this._version, tasks: normalizeOutline(tasks) };
    this._queued = true;
    clearTimeout(this._timer);
    this._timer = setTimeout(() => this._flush(), SAVE_DEBOUNCE_MS);
  }

  async _flush() {
    if (!this._queued || !this._version) return;
    this._queued = false;
    clearTimeout(this._timer);
    this._saving = true;
    const sent = this._version.tasks;
    try {
      const saved = await api.patch(
        `/estimate/templates/${this._tier}/versions/${this._version.id}`,
        { tasks: sent.map(toWire) },
      );
      // Only take the server's answer if nothing was typed while it was in flight; otherwise the
      // next flush carries the newer outline and this answer is stale.
      if (!this._queued) this._version = { ...saved, tasks: normalizeOutline(saved.tasks) };
      else this._version = { ...this._version, findings: saved.findings };
      this._error = null;
      api
        .get(`/estimate/templates/${this._tier}/versions/${this._version.id}/diff`)
        .then((d) => (this._diff = d))
        .catch(() => {});
    } catch (err) {
      // Keep the edit on screen and say the save failed; the next change carries it.
      this._queued = true;
      this._error = `Could not save that edit: ${err.message}`;
    } finally {
      this._saving = false;
    }
  }

  _patchTask(order, patch) {
    this._apply(
      this._version.tasks.map((t) => (t.display_order === order ? { ...t, ...patch } : t)),
    );
  }

  _patchLine(order, index, patch) {
    this._apply(
      this._version.tasks.map((t) =>
        t.display_order !== order
          ? t
          : {
              ...t,
              assignments: t.assignments.map((a, i) => (i === index ? { ...a, ...patch } : a)),
            },
      ),
    );
  }

  _addLine(order, serviceCode = null) {
    const task = this._version.tasks.find((t) => t.display_order === order);
    const used = new Set(task.assignments.map((a) => a.service_code));
    const code = serviceCode ?? this._services.find((s) => !used.has(s.service_code))?.service_code;
    if (!code || used.has(code)) return;
    const service = this._services.find((s) => s.service_code === code);
    // History's median for this service on this task, where it has one, so a line added from the
    // suggestion arrives with the number that suggested it.
    const suggested = this._historyFor(task)?.rung.services?.[code]?.median ?? 0;
    this._patchTask(order, {
      assignments: [
        ...task.assignments,
        {
          service_code: code,
          service_name: service?.service_name ?? null,
          hours: suggested,
          retired_from: null,
        },
      ],
    });
  }

  _removeLine(order, index) {
    const task = this._version.tasks.find((t) => t.display_order === order);
    this._patchTask(order, { assignments: task.assignments.filter((_, i) => i !== index) });
  }

  _add(after, parent, isGroup) {
    this._apply(addTask(this._version.tasks, { after, parent, is_group: isGroup }));
  }

  _remove(order) {
    const task = this._version.tasks.find((t) => t.display_order === order);
    const children = this._version.tasks.filter((t) => t.parent_order === order).length;
    if (
      task.is_group &&
      children &&
      !window.confirm(
        `Remove "${task.name}" and the ${children} task${children === 1 ? '' : 's'} in it?`,
      )
    )
      return;
    this._apply(removeTask(this._version.tasks, order));
  }

  _move(order, direction) {
    this._apply(moveTask(this._version.tasks, order, direction));
  }

  _reparent(order, parent) {
    this._apply(reparentTask(this._version.tasks, order, parent === '' ? null : Number(parent)));
  }

  _toggleGroup(order) {
    const next = new Set(this._collapsed);
    if (next.has(order)) next.delete(order);
    else next.add(order);
    this._collapsed = next;
  }

  _hidden(task) {
    const byOrder = new Map(this._version.tasks.map((t) => [t.display_order, t]));
    let parent = task.parent_order;
    for (let guard = 0; guard < 20 && parent !== null && parent !== undefined; guard++) {
      if (this._collapsed.has(parent)) return true;
      parent = byOrder.get(parent)?.parent_order ?? null;
    }
    return false;
  }

  _historyFor(task) {
    if (!this._history || task.is_group) return null;
    const key = task.match_key ?? null;
    return (
      this._history.tasks.find((h) => h.key === key) ??
      this._history.tasks.find((h) => h.name.toLowerCase() === task.name.trim().toLowerCase()) ??
      null
    );
  }

  /** The hard findings against a line, so the row can wear them. */
  _hardFor(order) {
    return (this._version?.findings?.hard ?? []).filter((f) => f.display_order === order);
  }

  // ------------------------------------------------------------------------------------------

  _headCard() {
    const t = this._template;
    const v = this._version;
    const hard = v?.findings?.hard ?? [];
    const soft = v?.findings?.soft ?? [];
    return html`
      <div class="card">
        <div class="eyebrow">
          <button class="link" @click=${() => navigate('/estimate/settings/templates')}>
            Schedule templates
          </button>
          · ${sizeTierLabel(this._tier)}
        </div>
        <h2>${sizeTierLabel(this._tier)} template</h2>
        <p class="sub">
          ${
            v
              ? v.status === 'draft'
                ? html`Draft <strong>version ${v.version_number}</strong>${
                      v.copied_from_id ? ` — ${v.note ?? ''}` : ''
                    }.
                    Nothing here reaches a schedule until it is published; Create Schedule keeps
                    cutting from
                    ${t.published ? `version ${t.published.version_number}` : 'nothing'} until then.`
                : v.status === 'published'
                  ? html`<strong>Version ${v.version_number}</strong> is what Create Schedule cuts
                      from${
                        v.published_at ? `, published ${since(v.published_at)}` : ''
                      }${v.published_by ? ` by ${v.published_by}` : ''}.${
                        v.origin === 'imported' && v.source_project_number
                          ? ` Imported from ${v.source_project_number}.`
                          : ''
                      }`
                  : html`<strong>Version ${v.version_number}</strong>,
                      superseded${v.superseded_at ? ` ${since(v.superseded_at)}` : ''}. Read-only;
                      restore it to a draft to bring it back.`
              : html`Nothing is published for this rung, so Create Schedule cannot cut a
                  ${sizeTierLabel(this._tier)} schedule. Start a draft, or import a Workamajig
                  project with <code>npm run templates:import</code>.`
          }
        </p>
        ${
          v
            ? html`<div class="totals">
                <div class="stat">
                  <span class="k">Hours</span>
                  <span class="v">${fmtHours(v.hours, { exact: true, unit: false })}</span>
                  <span class="n"
                    >${
                      this._history?.rung_median_hours
                        ? `history's median for this size: ${fmtHours(this._history.rung_median_hours)}`
                        : 'no history for this size yet'
                    }</span
                  >
                </div>
                <div class="stat">
                  <span class="k">Tasks</span>
                  <span class="v">${count(v.task_count)}</span>
                  <span class="n"
                    >${count(v.assignment_count)} line${v.assignment_count === 1 ? '' : 's'}</span
                  >
                </div>
                <div class="stat">
                  <span class="k">Span</span>
                  <span class="v">${count(v.span_weekdays)}</span>
                  <span class="n">working days, first task to last</span>
                </div>
                <div class="stat">
                  <span class="k">History</span>
                  <span class="v">${count(this._history?.reference_projects?.rung ?? 0)}</span>
                  <span class="n"
                    >${sizeTierLabel(this._tier)} schedules ·
                    ${count(this._history?.reference_projects?.pooled ?? 0)} of every size</span
                  >
                </div>
              </div>`
            : nothing
        }
        ${this._rangeRow()}
        ${
          hard.length
            ? html`<div class="findings hard" role="alert">
                <strong>This draft cannot be published yet.</strong> ${hard.length}
                thing${hard.length === 1 ? '' : 's'} to fix:
                <ul>
                  ${hard.map((f) => html`<li>${f.message}</li>`)}
                </ul>
              </div>`
            : nothing
        }
        ${
          soft.length
            ? html`<div class="findings">
                <strong>Worth a look, not a refusal.</strong>
                <ul>
                  ${soft.map((f) => html`<li>${f.message}</li>`)}
                </ul>
              </div>`
            : nothing
        }
        ${this._error ? html`<p class="error">${this._error}</p>` : nothing}
        <div class="actions">
          ${
            !this._isAdmin
              ? html`<span class="hint"
                  ><strong>Changing a template is an admin action; you are reading.</strong></span
                >`
              : v?.status === 'draft'
                ? html`
                    <input
                      type="text"
                      placeholder="Publish note (optional)"
                      style="width: 28ch"
                      .value=${this._publishNote}
                      @input=${(e) => (this._publishNote = e.target.value)}
                    />
                    <button
                      class="primary"
                      ?disabled=${this._busy || hard.length > 0}
                      title=${hard.length ? 'Fix the findings above first' : 'Make this the version Create Schedule cuts from'}
                      @click=${() => this._publish()}
                    >
                      Publish version ${v.version_number}
                    </button>
                    <button class="danger" ?disabled=${this._busy} @click=${() => this._discard()}>
                      Discard draft
                    </button>
                  `
                : t.draft
                  ? html`<button
                      class="primary"
                      ?disabled=${this._busy}
                      @click=${() => this._show(t.draft.id)}
                    >
                      Continue draft v${t.draft.version_number}
                    </button>`
                  : html`<button
                      class="primary"
                      ?disabled=${this._busy}
                      @click=${() => this._openDraft(v?.status === 'superseded' ? v.id : null)}
                    >
                      ${v?.status === 'superseded' ? `Restore version ${v.version_number} as a draft` : v ? 'Edit — open a draft' : 'Start a draft'}
                    </button>`
          }
        </div>
      </div>
    `;
  }

  _rangeRow() {
    const t = this._template;
    const r = this._range ?? { min: '', max: '' };
    const changed =
      !t.size_range || Number(r.min) !== t.size_range.min || Number(r.max) !== t.size_range.max;
    return html`
      <div class="range" style="margin-top: 12px">
        <span class="locked">Size axis</span>
        <input
          type="number"
          min="0.25"
          step="0.25"
          aria-label="Size range minimum, hours"
          .value=${String(r.min ?? '')}
          ?disabled=${!this._isAdmin || this._busy}
          @input=${(e) => (this._range = { ...r, min: e.target.value })}
        />
        <span>–</span>
        <input
          type="number"
          min="0.25"
          step="0.25"
          aria-label="Size range maximum, hours"
          .value=${String(r.max ?? '')}
          ?disabled=${!this._isAdmin || this._busy}
          @input=${(e) => (this._range = { ...r, max: e.target.value })}
        />
        <span class="hint">
          hours. Where this rung sits when a past project's size is derived (7.2). Editing the
          template's hours does not move it; this
          does${
            this._history
              ? `, and ${count(this._history.rung_projects)} past websites currently derive as ${sizeTierLabel(this._tier)}`
              : ''
          }.
        </span>
        ${
          this._isAdmin && changed && r.min !== '' && r.max !== ''
            ? html`<button ?disabled=${this._busy} @click=${() => this._saveRange()}>
                Set range
              </button>`
            : nothing
        }
      </div>
    `;
  }

  _outlineCard() {
    const v = this._version;
    const visible = v.tasks.filter((t) => !this._hidden(t));
    const groups = v.tasks.filter((t) => t.is_group);
    return html`
      <div class="card">
        <div class="toolbar">
          <h3>The outline</h3>
          ${this._editable ? nothing : html`<span class="locked">Read-only — ${v.status}</span>`}
          <label>
            <input
              type="checkbox"
              .checked=${this._showHistory}
              @change=${(e) => (this._showHistory = e.target.checked)}
            />
            Show history
          </label>
          ${
            this._editable
              ? html`<button
                  class="icon"
                  @click=${() => this._add(v.tasks[v.tasks.length - 1]?.display_order ?? null, null, true)}
                >
                  + Group at the end
                </button>`
              : nothing
          }
          ${this._saving ? html`<span class="saving">Saving…</span>` : nothing}
        </div>
        <div class="scroll">
          <table>
            <thead>
              <tr>
                <th>Task</th>
                <th>Timing</th>
                <th>Default assignments</th>
                ${this._showHistory ? html`<th>History</th>` : nothing}
                <th class="num">Hours</th>
                ${this._editable ? html`<th></th>` : nothing}
              </tr>
            </thead>
            <tbody>
              ${visible.map((t) => this._taskRow(t, groups))}
            </tbody>
          </table>
        </div>
        ${
          !v.tasks.length
            ? html`<p class="state">
                An empty outline. Add a group to
                begin${this._editable ? '' : ' — as an admin, on a draft'}.
              </p>`
            : nothing
        }
      </div>
    `;
  }

  _taskRow(task, groups) {
    const v = this._version;
    const editable = this._editable;
    const rollup = task.is_group
      ? v.tasks
          .filter((t) => this._isDescendant(t, task.display_order))
          .reduce((s, t) => s + t.hours, 0)
      : task.hours;
    const hard = this._hardFor(task.display_order);
    const history = this._historyFor(task);
    const rare = history && this._history.reference_projects.rung > 0 && history.rung.usage <= 0.05;
    return html`
      <tr class="${task.is_group ? 'group' : ''} ${hard.length ? 'flagged' : ''}">
        <td class="indent-${Math.min(task.task_level, 3)}">
          ${
            task.is_group
              ? html`<button
                  class="icon"
                  aria-expanded=${this._collapsed.has(task.display_order) ? 'false' : 'true'}
                  title="Collapse or expand"
                  @click=${() => this._toggleGroup(task.display_order)}
                >
                  ${this._collapsed.has(task.display_order) ? '+' : '−'}
                </button>`
              : nothing
          }
          <input
            class="name"
            aria-label="Task name"
            .value=${task.name}
            ?readonly=${!editable}
            @change=${(e) => this._patchTask(task.display_order, { name: e.target.value })}
          />
          ${hard.map((f) => html`<span class="mark hard" title=${f.message}>${f.rule.replace(/_/g, ' ')}</span>`)}
          ${rare ? html`<span class="mark warn" title="Used by ${history.rung.projects} of ${this._history.reference_projects.rung} ${sizeTierLabel(this._tier)} schedules">Rarely used</span>` : nothing}
        </td>
        <td>
          ${
            task.is_group
              ? html`<span class="hint">Spans its tasks</span>`
              : html`<div class="timing">
                  <span>ends</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    aria-label="Working days before the delivery date this task ends"
                    .value=${String(task.ends_before_delivery ?? '')}
                    ?disabled=${!editable}
                    @change=${(e) => this._patchTask(task.display_order, { ends_before_delivery: e.target.value === '' ? null : Number(e.target.value) })}
                  />
                  <span>d before delivery, lasts</span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    aria-label="Working days this task lasts"
                    .value=${String(task.duration_weekdays ?? '')}
                    ?disabled=${!editable}
                    @change=${(e) => this._patchTask(task.display_order, { duration_weekdays: e.target.value === '' ? null : Number(e.target.value) })}
                  />
                  <span>d</span>
                </div>`
          }
        </td>
        <td>
          ${
            task.is_group
              ? html`<span class="hint">Group — the sum of its tasks.</span>`
              : html`<div class="lines">
                  ${task.assignments.map((a, i) => this._line(task, a, i, history))}
                  ${
                    editable
                      ? html`<div class="line">
                          <button
                            class="icon"
                            title="Add a service to this task"
                            @click=${() => this._addLine(task.display_order)}
                          >
                            + line
                          </button>
                          ${this._suggestions(task, history)}
                        </div>`
                      : nothing
                  }
                </div>`
          }
        </td>
        ${
          this._showHistory
            ? html`<td class="hist">
                ${
                  task.is_group || !history
                    ? nothing
                    : html`${
                          this._history.reference_projects.rung
                            ? html`<span class=${rare ? 'rare' : ''}
                                >${history.rung.projects} of
                                ${this._history.reference_projects.rung}
                                ${sizeTierLabel(this._tier)} schedules</span
                              >`
                            : nothing
                        } <br />${history.pooled.projects} of
                        ${this._history.reference_projects.pooled} of every size`
                }
              </td>`
            : nothing
        }
        <td class="num">${fmtHours(rollup, { exact: true, unit: false })}</td>
        ${
          editable
            ? html`<td>
                <div class="rowtools">
                  <button
                    class="icon"
                    title="Move up"
                    aria-label="Move ${task.name} up"
                    @click=${() => this._move(task.display_order, 'up')}
                  >
                    ↑
                  </button>
                  <button
                    class="icon"
                    title="Move down"
                    aria-label="Move ${task.name} down"
                    @click=${() => this._move(task.display_order, 'down')}
                  >
                    ↓
                  </button>
                  <select
                    aria-label="Move ${task.name} into a group"
                    .value=${String(task.parent_order ?? '')}
                    @change=${(e) => this._reparent(task.display_order, e.target.value)}
                  >
                    <option value="" ?selected=${task.parent_order === null}>Top level</option>
                    ${groups
                      .filter(
                        (g) =>
                          g.display_order !== task.display_order &&
                          !this._isDescendant(g, task.display_order),
                      )
                      .map(
                        (g) =>
                          html`<option
                            value=${g.display_order}
                            ?selected=${g.display_order === task.parent_order}
                          >
                            in ${g.name}
                          </option>`,
                      )}
                  </select>
                  <button
                    class="icon"
                    title="Add a task after this"
                    @click=${() => this._add(task.display_order, task.is_group ? task.display_order : task.parent_order, false)}
                  >
                    + task
                  </button>
                  <button
                    class="icon"
                    title="Add a group after this"
                    @click=${() => this._add(task.display_order, task.is_group ? task.parent_order : task.parent_order, true)}
                  >
                    + group
                  </button>
                  <button
                    class="icon"
                    title=${task.is_group ? 'Remove this group and everything in it' : 'Remove this task'}
                    @click=${() => this._remove(task.display_order)}
                  >
                    Remove
                  </button>
                </div>
              </td>`
            : nothing
        }
      </tr>
    `;
  }

  _isDescendant(task, ancestorOrder) {
    const byOrder = new Map(this._version.tasks.map((t) => [t.display_order, t]));
    let p = task.parent_order;
    for (let guard = 0; guard < 20 && p !== null && p !== undefined; guard++) {
      if (p === ancestorOrder) return true;
      p = byOrder.get(p)?.parent_order ?? null;
    }
    return false;
  }

  /**
   * One default assignment: a service, its hours, and a way to drop it. Nothing here names a
   * person — there is no field for one (8.3).
   */
  _line(task, a, index, history) {
    const editable = this._editable;
    const rung = history?.rung.services?.[a.service_code] ?? null;
    const pooled = history?.pooled.services?.[a.service_code] ?? null;
    return html`
      <div class="line">
        <select
          aria-label="Service for ${task.name}"
          ?disabled=${!editable}
          @change=${(e) => this._patchLine(task.display_order, index, { service_code: e.target.value, service_name: this._services.find((s) => s.service_code === e.target.value)?.service_name ?? null })}
        >
          ${this._serviceOptions(a.service_code).map(
            (s) =>
              html`<option value=${s.service_code} ?selected=${s.service_code === a.service_code}>
                ${s.service_code}${s.service_name ? ` — ${s.service_name}` : ''}
              </option>`,
          )}
        </select>
        <input
          type="number"
          min="0"
          step="0.25"
          aria-label="Hours of ${a.service_code} on ${task.name}"
          ?disabled=${!editable}
          .value=${String(a.hours)}
          @change=${(e) => this._patchLine(task.display_order, index, { hours: Number(e.target.value), source: 'designer', source_sample: null })}
        />
        ${a.retired_from ? html`<span class="mark" title="${a.retired_from} has been deactivated in Workamajig; this line was re-homed on import">was ${a.retired_from}</span>` : nothing}
        ${sourceMark(a)}
        ${
          this._showHistory && (rung || pooled)
            ? html`<span
                class="hist"
                title="Median hours booked to this service on this task, and how many projects booked it"
              >
                ${rung ? html`<strong>${fmtHours(rung.median, { exact: true, unit: false })}</strong> ×${rung.sample} here` : nothing}${
                  rung && pooled ? ' · ' : ''
                }${pooled ? html`${fmtHours(pooled.median, { exact: true, unit: false })} ×${pooled.sample} all` : nothing}
              </span>`
            : nothing
        }
        ${editable ? html`<button class="icon" title="Remove this line" @click=${() => this._removeLine(task.display_order, index)}>×</button>` : nothing}
      </div>
    `;
  }

  /** Services history books to this task that the template does not carry, offered as lines. */
  _suggestions(task, history) {
    if (!history || !this._showHistory) return nothing;
    const used = new Set(task.assignments.map((a) => a.service_code));
    const live = new Set(this._services.map((s) => s.service_code));
    const minRefs = this._history.min_refs ?? 5;
    const offers = Object.entries(history.rung.services ?? {})
      .filter(
        ([code, s]) => !used.has(code) && live.has(code) && s.sample >= minRefs && s.median > 0,
      )
      .slice(0, 4);
    if (!offers.length) return nothing;
    return offers.map(
      ([code, s]) =>
        html`<button
          class="link"
          title="History books ${code} here in ${s.sample} ${sizeTierLabel(this._tier)} schedules; add it at the median"
          @click=${() => this._addLine(task.display_order, code)}
        >
          + ${code} ${fmtHours(s.median, { exact: true, unit: false })} h ×${s.sample}
        </button>`,
    );
  }

  _serviceOptions(current) {
    const list = this._services;
    if (!current || list.some((s) => s.service_code === current)) return list;
    return [{ service_code: current, service_name: null }, ...list];
  }

  _diffCard() {
    const d = this._diff;
    if (!d || this._version?.status !== 'draft') return nothing;
    const total =
      d.added.length + d.removed.length + d.renamed.length + d.rehoured.length + d.moved.length;
    return html`
      <div class="card">
        <h3>Against version ${d.against_version_number ?? '—'}</h3>
        ${
          d.unchanged
            ? html`<p class="hint">Nothing has changed yet.</p>`
            : html`<div class="diff">
                <span class="hint"
                  >${fmtHours(d.hours_before)} → ${fmtHours(d.hours_after)}, ${total}
                  change${total === 1 ? '' : 's'}</span
                >
                ${
                  d.added.length
                    ? html`<div>
                        <strong>Added</strong>
                        <ul>
                          ${d.added.map((l) => html`<li>${l.name} — ${fmtHours(l.hours)}</li>`)}
                        </ul>
                      </div>`
                    : nothing
                }
                ${
                  d.removed.length
                    ? html`<div>
                        <strong>Removed</strong>
                        <ul>
                          ${d.removed.map((l) => html`<li>${l.name} — ${fmtHours(l.hours)}</li>`)}
                        </ul>
                      </div>`
                    : nothing
                }
                ${
                  d.renamed.length
                    ? html`<div>
                        <strong>Renamed</strong>
                        <ul>
                          ${d.renamed.map((l) => html`<li>${l.from} → ${l.to}</li>`)}
                        </ul>
                      </div>`
                    : nothing
                }
                ${
                  d.rehoured.length
                    ? html`<div>
                        <strong>Hours changed</strong>
                        <ul>
                          ${d.rehoured.map((l) => html`<li>${l.name} · ${l.service_code}: ${l.from ?? '—'} → ${l.to ?? '—'}</li>`)}
                        </ul>
                      </div>`
                    : nothing
                }
                ${
                  d.moved.length
                    ? html`<div>
                        <strong>Moved</strong>
                        <ul>
                          ${d.moved.map((l) => html`<li>${l.name}: line ${l.from} → ${l.to}</li>`)}
                        </ul>
                      </div>`
                    : nothing
                }
              </div>`
        }
      </div>
    `;
  }

  _versionsCard() {
    const t = this._template;
    if (!t?.versions?.length) return nothing;
    return html`
      <div class="card">
        <h3>Versions</h3>
        <div class="versions">
          ${t.versions.map(
            (v) =>
              html`<div class="version">
                <strong>v${v.version_number}</strong>
                <span class="mark ${v.status === 'draft' ? 'warn' : ''}">${v.status}</span>
                <span>${fmtHours(v.hours)} · ${count(v.task_count)} tasks</span>
                <span class="n"
                  >${v.note ?? ''}${v.published_at ? ` · published ${since(v.published_at)}` : v.updated_at ? ` · edited ${since(v.updated_at)}` : ''}${v.published_by ? ` by ${v.published_by}` : ''}</span
                >
                ${this._viewing === v.id ? html`<span class="n">(showing)</span>` : html`<button class="link" @click=${() => this._show(v.id)}>view</button>`}
                ${
                  this._isAdmin && v.status === 'superseded' && !t.draft
                    ? html`<button class="link" @click=${() => this._openDraft(v.id)}>
                        restore as a draft
                      </button>`
                    : nothing
                }
              </div>`,
          )}
        </div>
      </div>
    `;
  }

  render() {
    if (this._loadError)
      return html`<div class="card"><p class="error">${this._loadError}</p></div>`;
    if (!this._template) return html`<div class="card"><p class="state">Loading…</p></div>`;
    return html`
      ${this._headCard()} ${this._version ? this._outlineCard() : nothing} ${this._diffCard()}
      ${this._versionsCard()}
    `;
  }
}

/** Where a line's number came from (E15), in a word, with the sample where there was one. */
const SOURCE_LABELS = {
  rung_median: 'rung median',
  pooled_scaled: 'pooled, scaled',
  template_scaled: 'template, scaled',
  template: 'template',
  added_from_history: 'from history',
  designer: 'edited',
};
const sourceMark = (a) =>
  a.source
    ? html`<span
        class="mark ${a.source === 'designer' ? 'warn' : ''}"
        title="Where this number came from: ${SOURCE_LABELS[a.source] ?? a.source}${
          a.source_sample ? `, over ${a.source_sample} projects` : ''
        }"
        >${SOURCE_LABELS[a.source] ?? a.source}${a.source_sample ? ` ×${a.source_sample}` : ''}</span
      >`
    : nothing;

/** The screen's outline to the API's: the fields the server stores, and nothing else. */
const toWire = (t) => ({
  lineage_id: t.lineage_id ?? null,
  display_order: t.display_order,
  parent_order: t.parent_order,
  is_group: Boolean(t.is_group),
  name: t.name,
  description: t.description ?? null,
  track_budget: Boolean(t.track_budget),
  ends_before_delivery: t.ends_before_delivery ?? null,
  duration_weekdays: t.duration_weekdays ?? null,
  assignments: (t.assignments ?? []).map((a) => ({
    service_code: a.service_code,
    service_name: a.service_name ?? null,
    hours: Number(a.hours) || 0,
    source: a.source ?? null,
    source_sample: a.source_sample ?? null,
  })),
});

customElements.define('estimate-template-designer', EstimateTemplateDesigner);
